import { useEffect, useState, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchAPIDataWhileSettingState } from '../../b2b-common/helpers';
import { fetchFrameworksData } from '../../../services/b2b-frameworks-apiService';
import useAccessControl from '../../b2b-users/hooks/useAccessControl';
import { GenFrameworksContext, useLoginContext } from '../../../App';

const useFetchAllFrameworks = () => {
    const { loginData } = useLoginContext();
    const { genericFrameworks } = useContext(GenFrameworksContext);
    const queryClient = useQueryClient();
    const [error, setError] = useState(null);

    const { checkAccess: checkIotAccess } = useAccessControl([{ productsRequired: ['B2B_IOT_FW'], rolesRequired: ['ADMIN', 'EDITOR'] }, { rolesRequired: ['SUPER_ADMIN', 'SUPER_EDITOR'] }]);
    const { checkAccess: checkVerticalAccess } = useAccessControl([{ productsRequired: ['B2B_VERTICALFW'], rolesRequired: ['ADMIN', 'EDITOR'] }, { rolesRequired: ['SUPER_ADMIN', 'SUPER_EDITOR'] }]);
    const { checkAccess: checkPartnerAccess } = useAccessControl([{ productsRequired: ['B2B_PARTNERSFW'], rolesRequired: ['ADMIN', 'EDITOR'] }, { rolesRequired: ['SUPER_ADMIN', 'SUPER_EDITOR'] }]);
    //const { checkAccess: checkSoftwareAccess } = useAccessControl([{ productsRequired: ['B2B_SOFTWAREFW'], rolesRequired: ['ADMIN', 'EDITOR'] }, { rolesRequired: ['SUPER_ADMIN', 'SUPER_EDITOR'] }]);
    const checkSoftwareAccess = () => false;

    const hasIotFrameworks = checkIotAccess();
    const hasVerticalFrameworks = checkVerticalAccess();
    const hasPartnerFrameworks = checkPartnerAccess();
    const hasSoftwareFrameworks = checkSoftwareAccess();

    const fetchAllFrameworks = async (frameworkSuite) => {
        try {
            const paramsArray = [
                frameworkSuite,
                loginData.accessToken,
                true, // fetch images
                true, // include level1s
            ];
            const fws = await fetchAPIDataWhileSettingState(fetchFrameworksData, paramsArray, null);
            return fws;
        } catch (error) {
            setError(error.message);
            throw error;
        }
    };

    const fetchFrameworks = async () => {
        const newFrameworks = [];
        newFrameworks.push(...genericFrameworks);

        if (hasIotFrameworks) {
            const iotFws = await fetchAllFrameworks('iot');
            iotFws.forEach(fw => {
                newFrameworks.push(fw);
            });
        }
        if (hasVerticalFrameworks) {
            const verticalFws = await fetchAllFrameworks('vertical');
            verticalFws.forEach(fw => {
                newFrameworks.push(fw);
            });
        }
        if (hasPartnerFrameworks) {
            const partnerFws = await fetchAllFrameworks('partner');
            partnerFws.forEach(fw => {
                newFrameworks.push(fw);
            });
        }
        if (hasSoftwareFrameworks) {
            const softwareFws = await fetchAllFrameworks('software');
            softwareFws.forEach(fw => {
                newFrameworks.push(fw);
            });
        }
        return newFrameworks;
    };

    const staleTimeMinutes = 30; // 30 minutes
    const staleTimeMilliseconds = staleTimeMinutes * 60 * 1000;

    const { data: frameworks, isFetching } = useQuery({
        queryKey: ['all-frameworks', loginData],
        queryFn: fetchFrameworks,
        staleTime: staleTimeMilliseconds,
        onSuccess: (newFrameworks) => {
            queryClient.setQueryData(['all-frameworks', loginData], (oldData) => ({
                ...oldData,
                all: oldData.all.concat(newFrameworks)
            }));
        },
        onError: (error) => {
            setError(error.message);
        }
    });

    useEffect(() => {
        if (error) {
            setError(error.message);
        }
    }, [error]);

    return { frameworks, isFetching, error };
};

export default useFetchAllFrameworks;